import { addMinutes, endOfToday, isAfter } from "date-fns";
import { createContext, useContext, useEffect, useState } from "react";
import { EPromotionType, IGET_RESTAURANT_PROMOTION } from "../graphql/customQueries";

import {
    ICartProduct,
    EOrderType,
    ICartItemQuantitiesById,
    ICartPromotion,
    CheckIfPromotionValidResponse,
    ICartPaymentAmounts,
    ICartPayment,
    EPaymentMethod,
    ICustomerInformation,
    IGuestInformation,
    IUBER_DIRECT_QUOTE_RESPONSE,
    IUBER_DIRECT_CREATE_DELIVERY_RESPONSE,
    EOnlineDeliveryProvider,
} from "../model/model";
import {
    applyDiscountToCartProducts,
    checkIfPromotionValid,
    convertPhoneNumberToIntlFormat,
    getOrderDiscountAmount,
    isOrderTypeAllowed,
} from "../util/util";
import { useRestaurant } from "./restaurant-context";
import { useRegister } from "./register-context";
import { getThirdPartyAPIEndpoint } from "../private/aws-custom";
import axios from "axios";
import { toast } from "../tabin/components/toast";

interface ICachedCart {
    restaurantId: string | null;
    orderType: EOrderType | null;
    products: ICartProduct[] | null;
    notes: string;
    // deliveryFee: number;
    // deliveryAddress: string;
    // deliveryAddressLat: number;
    // deliveryAddressLng: number;
    total: number;
    subTotal: number;
    guestInformation: IGuestInformation;
    expiry: string;
}

const initialParkedOrderId = null;
const initialParkedOrderNumber = null;
const initialOrderType = null;
const initialPaymentMethod = EPaymentMethod.EFTPOS;
const initialCovers = 1;
const initialTableNumber = undefined;
const initialBuzzerNumber = null;
const initialCustomerInformation = null;
const initialProducts = null;
const initialNotes = "";
const initialTotal = 0;
const initialSurcharge = 0;
const initialPaymentAmounts: ICartPaymentAmounts = { cash: 0, eftpos: 0, online: 0, uberEats: 0, menulog: 0 };
const initialSubTotal = 0;
const initialPayments = [];
const initialOrderTypeSurcharge = 0;
const initialTransactionEftposReceipts = "";
const initialIsShownUpSellCrossSellModal = false;
const initialIsShownOrderThresholdMessageModal = false;
const initialUserAppliedPromotionCode = null;
const initialPromotion = null;
const initialAvailablePromotions = [];
const initialCartCategoryQuantitiesById = {};
const initialCartProductQuantitiesById = {};
const initialCartModifierQuantitiesById = {};
const initialGuestInformation = {
    firstName: "",
    email: "",
    phoneNumber: "",
    firstNameError: "",
    emailError: "",
    phoneNumberError: "",
};
const initialOrderScheduledAt = null;
const initialDeliveryAddress = null;
const initialDeliveryAddressLat = null;
const initialDeliveryAddressLng = null;
const initialDeliveryNotes = null;
const initialDeliveryFeeDiscount = 0;
const initialDeliveryFee = 0;
const initialUberDeliveryQuoteId = null;

type IDeliveryAddressPayload = {
    street_address: string[];
    city: string;
    state: string;
    zip_code: string;
    country: string;
};

const isValidCoordinate = (lat?: number | null, lng?: number | null) => {
    if (typeof lat !== "number" || typeof lng !== "number") return false;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    return !(lat === 0 && lng === 0);
};

const hasDeliveryAddressFields = (address?: IDeliveryAddressPayload | null) => {
    if (!address) return false;
    return Boolean(address.street_address?.some((line) => line?.trim()) && address.city?.trim() && address.state?.trim() && address.zip_code?.trim() && address.country?.trim());
};

type ContextProps = {
    // restaurant: IGET_RESTAURANT | null;
    // setRestaurant: (restaurant: IGET_RESTAURANT) => void;
    parkedOrderId: string | null;
    setParkedOrderId: (parkedOrderId: string | null) => void;
    parkedOrderNumber: string | null;
    setParkedOrderNumber: (parkedOrderNumber: string | null) => void;
    orderType: EOrderType | null;
    setOrderType: (orderType: EOrderType) => void;
    paymentMethod: EPaymentMethod | null;
    setPaymentMethod: (paymentMethod: EPaymentMethod | null) => void;
    covers: number;
    setCovers: (covers: number) => void;
    tableNumber: string | null | undefined;
    setTableNumber: (tableNumber: string | null | undefined) => void;
    buzzerNumber: string | null;
    setBuzzerNumber: (buzzerNumber: string | null) => void;
    customerInformation: ICustomerInformation | null;
    setCustomerInformation: (customerInformation: ICustomerInformation | null) => void;
    products: ICartProduct[] | null;
    cartProductQuantitiesById: ICartItemQuantitiesById;
    cartModifierQuantitiesById: ICartItemQuantitiesById;
    setProducts: (products: ICartProduct[]) => void;
    addProduct: (product: ICartProduct) => void;
    updateProduct: (index: number, product: ICartProduct) => void;
    updateProductQuantity: (index: number, quantity: number) => void;
    applyProductDiscount: (index: number, discount: number) => void;
    deleteProduct: (index: number) => void; // has a index input because multiple products in cart could have the same id
    clearCart: () => void;
    notes: string;
    setNotes: (notes: string) => void;
    promotion: ICartPromotion | null;
    userAppliedPromotionCode: string | null;
    setUserAppliedPromotion: (promotion: IGET_RESTAURANT_PROMOTION) => CheckIfPromotionValidResponse;
    removeUserAppliedPromotion: () => void;
    total: number;
    surcharge: number;
    subTotal: number;
    paidSoFar: number;
    orderTypeSurcharge: number;
    payments: ICartPayment[];
    setPayments: (payment: ICartPayment[]) => void;
    paymentAmounts: ICartPaymentAmounts;
    setPaymentAmounts: (paymentAmounts: ICartPaymentAmounts) => void;
    transactionEftposReceipts: string;
    setTransactionEftposReceipts: (receipt: string) => void;
    isShownUpSellCrossSellModal: boolean;
    setIsShownUpSellCrossSellModal: (isShownUpSellCrossSellModal: boolean) => void;
    isShownOrderThresholdMessageModal: boolean;
    setIsShownOrderThresholdMessageModal: (isShownOrderThresholdMessageModal: boolean) => void;
    orderScheduledAt: string | null;
    updateOrderScheduledAt: (orderScheduledAt: string | null) => void;
    guestInformation: IGuestInformation;
    setGuestInformation: (guestInformation: IGuestInformation) => void;
    bugFixRecalculateTotal: (products: ICartProduct[] | null) => number;
    bugFixRecalculateSubTotalAndSurcharge: (inputTotal: number) => { subTotal: number; surcharge: number };
    deliveryAddress: IDeliveryAddressPayload | null;
    deliveryNotes: string | null;
    deliveryProvider: EOnlineDeliveryProvider | null;
    deliveryDistanceMeters: number | null;
    deliveryFeeDiscount: number;
    deliveryFee: number;
    setDeliveryAddressAndProcessQuote: (
        deliveryAddress: IDeliveryAddressPayload,
        deliveryAddressLat: number,
        deliveryAddressLng: number,
        deliveryNotes: string,
    ) => Promise<{ error: string | null; quoteResponse: IUBER_DIRECT_QUOTE_RESPONSE | null }>;
    getUberDirectDeliveryPayload: () => string | null;
};

const CartContext = createContext<ContextProps>({
    // restaurant: initialRestaurant,
    // setRestaurant: () => {},
    parkedOrderId: initialParkedOrderId,
    setParkedOrderId: () => {},
    parkedOrderNumber: initialParkedOrderNumber,
    setParkedOrderNumber: () => {},
    orderType: initialOrderType,
    setOrderType: () => {},
    paymentMethod: initialPaymentMethod,
    setPaymentMethod: () => {},
    tableNumber: initialTableNumber,
    setTableNumber: () => {},
    covers: initialCovers,
    setCovers: () => {},
    buzzerNumber: initialBuzzerNumber,
    setBuzzerNumber: () => {},
    customerInformation: initialCustomerInformation,
    setCustomerInformation: () => {},
    products: initialProducts,
    cartProductQuantitiesById: {},
    cartModifierQuantitiesById: {},
    setProducts: () => {},
    addProduct: () => {},
    updateProduct: () => {},
    updateProductQuantity: () => {},
    applyProductDiscount: () => {},
    deleteProduct: () => {},
    clearCart: () => {},
    notes: initialNotes,
    setNotes: () => {},
    promotion: initialPromotion,
    userAppliedPromotionCode: "",
    setUserAppliedPromotion: () => CheckIfPromotionValidResponse.VALID,
    removeUserAppliedPromotion: () => {},
    total: initialTotal,
    surcharge: initialSurcharge,
    subTotal: initialSubTotal,
    paidSoFar: 0,
    orderTypeSurcharge: initialOrderTypeSurcharge,
    payments: initialPayments,
    setPayments: () => {},
    paymentAmounts: initialPaymentAmounts,
    setPaymentAmounts: () => {},
    transactionEftposReceipts: initialTransactionEftposReceipts,
    setTransactionEftposReceipts: () => {},
    isShownUpSellCrossSellModal: initialIsShownUpSellCrossSellModal,
    isShownOrderThresholdMessageModal: initialIsShownOrderThresholdMessageModal,
    setIsShownUpSellCrossSellModal: () => {},
    setIsShownOrderThresholdMessageModal: () => {},
    orderScheduledAt: initialOrderScheduledAt,
    updateOrderScheduledAt: (orderScheduledAt: string | null) => {},
    guestInformation: initialGuestInformation,
    setGuestInformation: () => {},
    bugFixRecalculateTotal: (products: ICartProduct[] | null) => 0,
    bugFixRecalculateSubTotalAndSurcharge: (inputTotal: number) => ({
        subTotal: 0,
        surcharge: 0,
    }),
    deliveryAddress: null,
    deliveryNotes: null,
    deliveryProvider: null,
    deliveryDistanceMeters: null,
    deliveryFeeDiscount: 0,
    deliveryFee: 0,
    setDeliveryAddressAndProcessQuote: (
        deliveryAddress: IDeliveryAddressPayload,
        deliveryAddressLat: number,
        deliveryAddressLng: number,
        deliveryNotes: string,
    ) => new Promise(() => {}),
    getUberDirectDeliveryPayload: () => null,
});

const CartProvider = (props: { children: React.ReactNode }) => {
    const { restaurant, menuProducts, menuModifiers, menuCategories } = useRestaurant();
    const { register } = useRegister();

    const [parkedOrderId, _setParkedOrderId] = useState<string | null>(initialParkedOrderId);
    const [parkedOrderNumber, _setParkedOrderNumber] = useState<string | null>(initialParkedOrderNumber);
    const [orderType, _setOrderType] = useState<EOrderType | null>(initialOrderType);
    const [paymentMethod, _setPaymentMethod] = useState<EPaymentMethod | null>(initialPaymentMethod);
    const [covers, _setCovers] = useState<number>(initialCovers);
    const [tableNumber, _setTableNumber] = useState<string | null | undefined>(initialTableNumber);
    const [buzzerNumber, _setBuzzerNumber] = useState<string | null>(initialBuzzerNumber);
    const [customerInformation, _setCustomerInformation] = useState<ICustomerInformation | null>(initialCustomerInformation);
    const [products, _setProducts] = useState<ICartProduct[] | null>(initialProducts);
    const [notes, _setNotes] = useState<string>(initialNotes);
    const [total, _setTotal] = useState<number>(initialTotal);
    const [surcharge, _setSurcharge] = useState<number>(initialSurcharge);
    const [paymentAmounts, _setPaymentAmounts] = useState<ICartPaymentAmounts>(initialPaymentAmounts);
    const [subTotal, _setSubTotal] = useState<number>(initialSubTotal);
    const [payments, _setPayments] = useState<ICartPayment[]>(initialPayments);
    const [orderTypeSurcharge, _setOrderTypeSurcharge] = useState<number>(initialOrderTypeSurcharge);
    const [transactionEftposReceipts, _setTransactionEftposReceipts] = useState<string>(initialTransactionEftposReceipts);
    const [isShownUpSellCrossSellModal, _setIsShownUpSellCrossSellModal] = useState<boolean>(initialIsShownUpSellCrossSellModal);
    const [isShownOrderThresholdMessageModal, _setIsShownOrderThresholdMessageModal] = useState(initialIsShownOrderThresholdMessageModal);

    const [userAppliedPromotionCode, _setUserAppliedPromotionCode] = useState<string | null>(initialUserAppliedPromotionCode);
    const [promotion, _setPromotion] = useState<ICartPromotion | null>(initialPromotion);
    const [availablePromotions, _setAvailablePromotions] = useState<IGET_RESTAURANT_PROMOTION[]>(initialAvailablePromotions);

    const [cartCategoryQuantitiesById, _setCartCategoryQuantitiesById] = useState<ICartItemQuantitiesById>(initialCartCategoryQuantitiesById);
    const [cartProductQuantitiesById, _setCartProductQuantitiesById] = useState<ICartItemQuantitiesById>(initialCartProductQuantitiesById);
    const [cartModifierQuantitiesById, _setCartModifierQuantitiesById] = useState<ICartItemQuantitiesById>(initialCartModifierQuantitiesById);

    const [guestInformation, _setGuestInformation] = useState<IGuestInformation>(initialGuestInformation);
    const [orderScheduledAt, _setOrderScheduledAt] = useState<string | null>(initialOrderScheduledAt);

    const [deliveryAddress, setDeliveryAddress] = useState<IDeliveryAddressPayload | null>(initialDeliveryAddress);
    const [deliveryAddressLat, setDeliveryAddressLat] = useState<number | null>(initialDeliveryAddressLat);
    const [deliveryAddressLng, setDeliveryAddressLng] = useState<number | null>(initialDeliveryAddressLng);
    const [deliveryNotes, setDeliveryNotes] = useState<string | null>(initialDeliveryNotes);
    const [deliveryFeeDiscount, setDeliveryFeeDiscount] = useState<number>(initialDeliveryFeeDiscount);
    const [deliveryFee, setDeliveryFee] = useState<number>(initialDeliveryFee);
    const [uberDeliveryQuoteId, setUberDeliveryQuoteId] = useState<string | null>(initialUberDeliveryQuoteId);
    const [deliveryProvider, setDeliveryProvider] = useState<EOnlineDeliveryProvider | null>(null);
    const [deliveryDistanceMeters, setDeliveryDistanceMeters] = useState<number | null>(null);

    const [deliveryQuoteTimerMinutes, setDeliveryQuoteTimerMinutes] = useState<number>(0);
    const [isRunningdeliveryQuoteTimer, setIsRunningdeliveryQuoteTimer] = useState<boolean>(false);

    const calculateDeliveryFeeDiscount = (deliveryFeeQuote: number, newTotal: number) => {
        if (!restaurant) return;

        const onlineDeliveryRestaurantContributionPercentage = restaurant.onlineDeliveryRestaurantContributionPercentage ?? 0;
        const onlineDeliveryRestaurantContributionStatic = restaurant.onlineDeliveryRestaurantContributionStatic ?? 0;
        const onlineDeliveryTabinContributionPercentage = restaurant.onlineDeliveryTabinContributionPercentage ?? 0;
        const onlineDeliveryTabinContributionStatic = restaurant.onlineDeliveryTabinContributionStatic ?? 0;
        const onlineDeliveryContributionMinimumAmount = restaurant.onlineDeliveryContributionMinimumAmount ?? 0;
        const meetsContributionMinimum = newTotal >= onlineDeliveryContributionMinimumAmount;

        let totalDeliveryContributionDiscount = 0;

        if (meetsContributionMinimum) {
            const totalDeliveryContributionPercentage =
                onlineDeliveryRestaurantContributionPercentage / 100 + onlineDeliveryTabinContributionPercentage / 100;
            const totalDeliveryContributionStatic = onlineDeliveryRestaurantContributionStatic + onlineDeliveryTabinContributionStatic;

            totalDeliveryContributionDiscount = Math.floor(deliveryFeeQuote * totalDeliveryContributionPercentage + totalDeliveryContributionStatic);
        }
        let deliveryFeeAfterContributions = Math.ceil(deliveryFeeQuote - totalDeliveryContributionDiscount);

        if (deliveryFeeAfterContributions < 0) deliveryFeeAfterContributions = 0;

        setDeliveryFeeDiscount(totalDeliveryContributionDiscount);
        setDeliveryFee(deliveryFeeAfterContributions);
    };

    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;

        if (isRunningdeliveryQuoteTimer) {
            interval = setInterval(() => {
                setDeliveryQuoteTimerMinutes((prev) => prev + 1);

                if (deliveryQuoteTimerMinutes === 5) {
                    if (!deliveryAddress || deliveryAddressLat == null || deliveryAddressLng == null || !uberDeliveryQuoteId) return;

                    setDeliveryAddressAndProcessQuote(deliveryAddress, deliveryAddressLat, deliveryAddressLng, deliveryNotes || "");
                    setDeliveryQuoteTimerMinutes(0);
                }
            }, 60 * 1000); //1 minute
        } else if (!isRunningdeliveryQuoteTimer && interval !== null) {
            clearInterval(interval);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isRunningdeliveryQuoteTimer, deliveryQuoteTimerMinutes]);

    const startDeliveryQuoteTimer = () => {
        setDeliveryQuoteTimerMinutes(0);
        setIsRunningdeliveryQuoteTimer(true);
    };

    const stopDeliveryQuoteTimer = () => setIsRunningdeliveryQuoteTimer(false);

    // useEffect(() => {
    // console.log("xxx...products", products);
    // }, [products]);

    //TODO Temp fix
    const bugFixRecalculateTotal = (products: ICartProduct[] | null) => {
        let totalPrice = 0;

        products &&
            products.forEach((p) => {
                let price = p.price;

                p.modifierGroups.forEach((mg) => {
                    mg.modifiers.forEach((m) => {
                        const changedQuantity = m.quantity - m.preSelectedQuantity;

                        if (changedQuantity > 0) {
                            price += m.price * changedQuantity;
                        }

                        if (m.productModifiers) {
                            m.productModifiers.forEach((productModifier) => {
                                productModifier.modifierGroups.forEach((orderedProductModifierModifierGroup) => {
                                    orderedProductModifierModifierGroup.modifiers.forEach((orderedProductModifierModifier) => {
                                        const changedQuantity =
                                            orderedProductModifierModifier.quantity - orderedProductModifierModifier.preSelectedQuantity;

                                        if (changedQuantity > 0) {
                                            price += orderedProductModifierModifier.price * changedQuantity;
                                        }
                                    });
                                });
                            });
                        }
                    });
                });

                totalPrice += price * p.quantity;
            });

        return totalPrice;
    };

    //TODO Temp fix
    const bugFixRecalculateSubTotalAndSurcharge = (inputTotal: number) => {
        let newSubTotal = inputTotal;
        let newSurcharge = 0;
        if (promotion) {
            if (promotion.discountedAmount > newSubTotal) {
                newSubTotal = 0;
            } else {
                newSubTotal -= promotion.discountedAmount;
            }
        }

        if (paymentMethod !== EPaymentMethod.LATER) {
            if (register && register.surchargePercentage) {
                newSurcharge += Math.round((newSubTotal * register.surchargePercentage) / 100);
            }
        }

        if (restaurant && restaurant.surchargePercentage) {
            newSurcharge += Math.round((newSubTotal * restaurant.surchargePercentage) / 100);
        }

        return {
            surcharge: newSurcharge,
            subTotal: newSubTotal + newSurcharge + orderTypeSurcharge + deliveryFee,
        };
    };

    useEffect(() => {
        if (!products) return;

        let newSubTotal = total;
        let newSurcharge = 0;
        if (promotion) {
            if (promotion.discountedAmount > newSubTotal) {
                newSubTotal = 0;
            } else {
                newSubTotal -= promotion.discountedAmount;
            }
        }

        if (paymentMethod !== EPaymentMethod.LATER) {
            if (register && register.surchargePercentage) {
                newSurcharge += Math.round((newSubTotal * register.surchargePercentage) / 100);
            }
        }

        if (restaurant && restaurant.surchargePercentage) {
            newSurcharge += Math.round((newSubTotal * restaurant.surchargePercentage) / 100);
        }

        _setSurcharge(newSurcharge);
        _setSubTotal(newSubTotal + newSurcharge + orderTypeSurcharge + deliveryFee);
    }, [total, promotion, restaurant, paymentMethod, orderTypeSurcharge, deliveryFee]);

    useEffect(() => {
        if (userAppliedPromotionCode) return; //Only apply restaurant promos if user has not applied one themselves
        const availPromotions: IGET_RESTAURANT_PROMOTION[] = [];

        restaurant &&
            restaurant.promotions.items.forEach((promotion) => {
                if (!promotion.autoApply) return;

                const status = checkIfPromotionValid(promotion);

                if (status !== CheckIfPromotionValidResponse.VALID) return;

                availPromotions.push(promotion);
            });

        _setAvailablePromotions(availPromotions);
    }, [restaurant, userAppliedPromotionCode]);

    useEffect(() => {
        if (!products) return;

        processPromotions(products, total);
    }, [userAppliedPromotionCode]);

    // TODO: Have to check many things for this to work.
    // #1. If the restaurant is the same as the products in cache.
    // #2. If the products are available (not sold out, quantity available, not deleted etc...)
    useEffect(() => {
        if (!restaurant) return;

        const cachedCart = sessionStorage.getItem("cart");

        if (cachedCart) {
            const cart: ICachedCart = JSON.parse(cachedCart);

            const now = new Date();
            const expiry = new Date(cart.expiry);
            if (isAfter(now, expiry)) {
                clearCart();
                return;
            }

            if (cart.restaurantId != restaurant.id) {
                clearCart();
                return;
            }

            _setOrderType(cart.orderType);
            _setProducts(cart.products);
            _setNotes(cart.notes);
            // setDeliveryFee(cart.deliveryFee);
            // setDeliveryAddress(cart.deliveryAddress);
            // setDeliveryAddressLat(cart.deliveryAddressLat);
            // setDeliveryAddressLng(cart.deliveryAddressLng);
            _setTotal(cart.total);
            _setSubTotal(cart.subTotal);
            _setGuestInformation(cart.guestInformation);

            updateCartQuantities(cart.products);

            // if (cart.orderType === EOrderType.DELIVERY) {
            //     if (cart.deliveryAddress) {
            //         setDeliveryAddressAndProcessQuote(cart.deliveryAddress, cart.deliveryAddressLat, cart.deliveryAddressLng);
            //     }
            // }
        }
    }, [restaurant]);

    const saveCartToCache = (products: ICartProduct[] | null) => {
        const total = recalculateTotal(products);

        const cacheCart: ICachedCart = {
            restaurantId: restaurant ? restaurant.id : null,
            orderType: orderType,
            products: products,
            notes: notes,
            // deliveryFee: deliveryFee || 0,
            // deliveryAddress: deliveryAddress || "",
            // deliveryAddressLat: deliveryAddressLat || 0,
            // deliveryAddressLng: deliveryAddressLng || 0,
            total: total,
            subTotal: subTotal,
            guestInformation: guestInformation,
            expiry: endOfToday().toISOString(),
        };

        sessionStorage.setItem("cart", JSON.stringify(cacheCart));
    };

    const removeCartFromCache = () => {
        sessionStorage.removeItem("cart");
    };

    useEffect(() => {
        if (!products) return;

        processPromotions(products, total);
    }, [userAppliedPromotionCode]);

    //This function should be a useEffect hook. But cannot make this because it cause infinite state change loop issue
    const processPromotions = (products: ICartProduct[], newTotal: number, newOrderType?: EOrderType) => {
        //Passing in newTotal here because if we take it from state it returns old total value.
        let bestPromotion: ICartPromotion | null = null;
        const odrType = newOrderType || orderType;

        let productsCpy: ICartProduct[] = JSON.parse(JSON.stringify(products));

        productsCpy = productsCpy.map((p, index) => ({
            ...p,
            index,
        }));
        availablePromotions.forEach((promotion) => {
            if (!odrType || !promotion.availableOrderTypes) return;
            if (!promotion.availableOrderTypes.includes(EOrderType[odrType])) return;
            if (promotion.totalAvailableUses !== null) {
                if (promotion.totalNumberUsed >= promotion.totalAvailableUses) return;
            }
            //We need the most up to date total amount
            if (newTotal < promotion.minSpend) {
                _setProducts(products);
                _setPromotion(null);
                return;
            }

            const discount = getOrderDiscountAmount(promotion, productsCpy, newTotal);

            if (!discount || discount.discountedAmount <= 0) return;

            if (!bestPromotion || discount.discountedAmount > bestPromotion.discountedAmount) {
                bestPromotion = {
                    discountedAmount: discount.discountedAmount,
                    matchingProducts: discount.matchingProducts,
                    promotion: promotion,
                };
            }
        });

        console.log("xxx...bestPromotion", bestPromotion);

        //If bestPromotion is null, the function will still reset all the product.discount values to 0
        const discountedProducts = applyDiscountToCartProducts(bestPromotion, products);
        console.log("bestPromotion", bestPromotion);
        _setProducts(discountedProducts);
        _setPromotion(bestPromotion);
    };

    const setUserAppliedPromotion = (promotion: IGET_RESTAURANT_PROMOTION): CheckIfPromotionValidResponse => {
        if (!products) return CheckIfPromotionValidResponse.UNAVAILABLE;

        const status =
            promotion.startDate == null || promotion.endDate == null ? CheckIfPromotionValidResponse.VALID : checkIfPromotionValid(promotion);

        if (status !== CheckIfPromotionValidResponse.VALID) return status;

        _setAvailablePromotions([promotion]);
        _setUserAppliedPromotionCode(promotion.code);

        return CheckIfPromotionValidResponse.VALID;
    };

    const removeUserAppliedPromotion = () => {
        _setUserAppliedPromotionCode(null);
        _setAvailablePromotions([]);
    };

    const updateCartQuantities = (products: ICartProduct[] | null) => {
        const newCartCategoryQuantitiesById: ICartItemQuantitiesById = {};
        const newCartProductQuantitiesById: ICartItemQuantitiesById = {};
        const newCartModifierQuantitiesById: ICartItemQuantitiesById = {};

        products &&
            products.forEach((product) => {
                if (!product.category) return; //Product will not have a product.category only if its productModifier

                if (newCartCategoryQuantitiesById[product.category.id]) {
                    //We use product.quantity here because category does not have quantity assigned to it. The number of products select is same as the quantity for the category.
                    newCartCategoryQuantitiesById[product.category.id].quantity += product.quantity;
                } else {
                    newCartCategoryQuantitiesById[product.category.id] = {
                        id: product.category.id,
                        name: product.category.name,
                        quantity: product.quantity,
                        price: product.price,
                        discount: 0,
                        categoryId: null,
                    };
                }
                //We do this because there could be the same product in the products array twice.
                if (newCartProductQuantitiesById[product.id]) {
                    newCartProductQuantitiesById[product.id].quantity += product.quantity;
                } else {
                    const modifiers: {
                        id: string;
                        quantity: number;
                        price: number;
                    }[] = [];

                    product.modifierGroups.forEach((modifierGroup) => {
                        modifierGroup.modifiers.forEach((modifier) => {
                            modifiers.push({ id: modifier.id, quantity: modifier.quantity, price: modifier.price });
                        });
                    });
                    newCartProductQuantitiesById[product.id] = {
                        id: product.id,
                        name: product.name,
                        quantity: product.quantity,
                        price: product.price,
                        discount: 0,
                        categoryId: product.category.id,
                    };
                }

                product.modifierGroups.forEach((modifierGroup) => {
                    modifierGroup.modifiers.forEach((modifier) => {
                        //Not sure if we should be calculating quantity of productModifiers.
                        // if (modifier.productModifiers) {
                        //     modifier.productModifiers.forEach((productModifier) => {
                        //         if (newCartProductQuantitiesById[productModifier.id]) {
                        //             newCartProductQuantitiesById[productModifier.id].quantity += product.quantity * modifier.quantity;
                        //         } else {
                        //             newCartProductQuantitiesById[productModifier.id] = {
                        //                 id: product.id,
                        //                 name: product.name,
                        //                 quantity: product.quantity,
                        //                 price: product.price,
                        //                 categoryId: product.category.id,
                        //             };
                        //         }
                        //     });
                        // } else {
                        if (newCartModifierQuantitiesById[modifier.id]) {
                            newCartModifierQuantitiesById[modifier.id].quantity += product.quantity * modifier.quantity;
                        } else {
                            newCartModifierQuantitiesById[modifier.id] = {
                                id: product.id,
                                name: product.name,
                                quantity: product.quantity,
                                price: product.price,
                                discount: 0,
                                categoryId: null,
                            };
                        }
                        // }
                    });
                });
            });

        _setCartCategoryQuantitiesById(newCartCategoryQuantitiesById);
        _setCartProductQuantitiesById(newCartProductQuantitiesById);
        _setCartModifierQuantitiesById(newCartModifierQuantitiesById);
    };

    const recalculateTotal = (products: ICartProduct[] | null) => {
        let totalPrice = 0;

        products &&
            products.forEach((p) => {
                let price = p.price;

                p.modifierGroups.forEach((mg) => {
                    mg.modifiers.forEach((m) => {
                        const changedQuantity = m.quantity - m.preSelectedQuantity;

                        if (changedQuantity > 0) {
                            price += m.price * changedQuantity;
                        }

                        if (m.productModifiers) {
                            m.productModifiers.forEach((productModifier) => {
                                productModifier.modifierGroups.forEach((orderedProductModifierModifierGroup) => {
                                    orderedProductModifierModifierGroup.modifiers.forEach((orderedProductModifierModifier) => {
                                        const changedQuantity =
                                            orderedProductModifierModifier.quantity - orderedProductModifierModifier.preSelectedQuantity;

                                        if (changedQuantity > 0) {
                                            price += orderedProductModifierModifier.price * changedQuantity;
                                        }
                                    });
                                });
                            });
                        }
                    });
                });

                totalPrice += price * p.quantity;
            });

        return totalPrice;
    };

    const setParkedOrderId = (parkedOrderId: string | null) => {
        _setParkedOrderId(parkedOrderId);
    };

    const setParkedOrderNumber = (parkedOrderNumber: string | null) => {
        _setParkedOrderNumber(parkedOrderNumber);
    };

    const setOrderType = (newOrderType: EOrderType) => {
        if (orderType === newOrderType) return;

        const getOrderTypePrice = (catalogItem: { price?: number | null; deliveryPrice?: number | null } | undefined, fallback: number) => {
            if (newOrderType === EOrderType.DELIVERY) {
                return catalogItem?.deliveryPrice ?? catalogItem?.price ?? fallback;
            }

            return catalogItem?.price ?? fallback;
        };

        const isCartProductAllowed = (cartProduct: ICartProduct) => {
            if (menuProducts[cartProduct.id] && !isOrderTypeAllowed(newOrderType, menuProducts[cartProduct.id].availableOrderTypes)) return false;

            if (cartProduct.category?.id) {
                if (
                    menuCategories[cartProduct.category?.id] &&
                    !isOrderTypeAllowed(newOrderType, menuCategories[cartProduct.category?.id].availableOrderTypes)
                )
                    return false;
            }

            return true;
        };

        const removedProductNames = new Set<string>();
        const cartAllowedProducts = (products ?? []).filter((cartProduct) => {
            const isAllowed = isCartProductAllowed(cartProduct);
            if (!isAllowed) {
                removedProductNames.add(cartProduct.name);
            }
            return isAllowed;
        });

        if (removedProductNames.size > 0) {
            const uniqueNames = Array.from(removedProductNames);
            const orderTypeLabel = newOrderType.toLowerCase();
            if (uniqueNames.length === 1) {
                toast.error(`${uniqueNames[0]} was removed because it isn't available for ${orderTypeLabel} orders.`);
            } else {
                toast.error(`${uniqueNames.join(", ")} were removed because they aren't available for ${orderTypeLabel} orders.`);
            }
        }

        // const order_type_surcharge = register?.orderTypeSurcharge != null ? register?.orderTypeSurcharge[orderType.toLocaleLowerCase()] : 0; //TODO Later
        const order_type_surcharge = 0;
        _setOrderType(newOrderType);
        _setOrderTypeSurcharge(order_type_surcharge);
        setDeliveryFee(initialDeliveryFee);
        setDeliveryFeeDiscount(initialDeliveryFeeDiscount);
        setUberDeliveryQuoteId(initialUberDeliveryQuoteId);
        setDeliveryProvider(null);
        setDeliveryDistanceMeters(null);
        stopDeliveryQuoteTimer();
        setBuzzerNumber(null);

        const updateProductForOrderType = (cartProduct: ICartProduct): ICartProduct => {
            const productCatalog = menuProducts[cartProduct.id];

            const updatedModifierGroups = cartProduct.modifierGroups.map((modifierGroup) => ({
                ...modifierGroup,
                modifiers: modifierGroup.modifiers.map((modifier) => {
                    const modifierCatalog = menuModifiers[modifier.id];
                    const filteredProductModifiers = modifier.productModifiers
                        ? modifier.productModifiers.filter((pm) => isCartProductAllowed(pm))
                        : null;

                    const updatedProductModifiers = filteredProductModifiers
                        ? filteredProductModifiers.map((productModifier) => {
                              const productModifierCatalog = menuProducts[productModifier.id];
                              const updatedProductModifierGroups = productModifier.modifierGroups
                                  ? productModifier.modifierGroups.map((pmGroup) => ({
                                        ...pmGroup,
                                        modifiers: pmGroup.modifiers.map((pmModifier) => {
                                            const pmModifierCatalog = menuModifiers[pmModifier.id];
                                            return {
                                                ...pmModifier,
                                                price: getOrderTypePrice(pmModifierCatalog, pmModifier.price),
                                            };
                                        }),
                                    }))
                                  : [];

                              return {
                                  ...productModifier,
                                  price: getOrderTypePrice(productModifierCatalog, productModifier.price),
                                  modifierGroups: updatedProductModifierGroups,
                              };
                          })
                        : null;

                    return {
                        ...modifier,
                        price: getOrderTypePrice(modifierCatalog, modifier.price),
                        productModifiers: updatedProductModifiers,
                    };
                }),
            }));

            return {
                ...cartProduct,
                price: getOrderTypePrice(productCatalog, cartProduct.price),
                modifierGroups: updatedModifierGroups,
            };
        };

        if (cartAllowedProducts.length > 0) {
            const updatedProducts = cartAllowedProducts.map(updateProductForOrderType);
            const newTotal = recalculateTotal(updatedProducts);
            _setProducts(updatedProducts);
            _setTotal(newTotal);
            updateCartQuantities(updatedProducts);
            processPromotions(updatedProducts, newTotal, newOrderType);
        } else if (products && products.length > 0) {
            _setProducts([]);
            _setTotal(0);
            updateCartQuantities([]);
            processPromotions([], 0, newOrderType);
        } else if (products) {
            processPromotions(products, total, newOrderType);
        }
    };

    const setDeliveryAddressAndProcessQuote = async (
        deliveryAddress: IDeliveryAddressPayload,
        deliveryAddressLat: number,
        deliveryAddressLng: number,
        deliveryNotes: string,
    ) => {
        if (!restaurant) return { error: "Restaurant is invalid", quoteResponse: null };
        if (!deliveryAddress || deliveryAddressLat == null || deliveryAddressLng == null)
            return { error: "Please select a valid delivery address from the dropdown list.", quoteResponse: null };
        if (!hasDeliveryAddressFields(deliveryAddress) || !isValidCoordinate(deliveryAddressLat, deliveryAddressLng))
            return { error: "Please select a valid delivery address from the dropdown list.", quoteResponse: null };

        try {
            setDeliveryNotes(deliveryNotes);

            const pickupAddressPayload = {
                street_address: restaurant.address.aptSuite
                    ? [restaurant.address.streetAddress, restaurant.address.aptSuite]
                    : [restaurant.address.streetAddress],
                city: restaurant.address.city,
                state: restaurant.address.state,
                zip_code: restaurant.address.zipCode,
                country: restaurant.address.country,
            };

            if (!hasDeliveryAddressFields(pickupAddressPayload) || !isValidCoordinate(restaurant._geoloc.lat, restaurant._geoloc.lng)) {
                return { error: "Restaurant delivery pickup address is not configured. Please contact the restaurant.", quoteResponse: null };
            }

            const payload = {
                pickup_address: JSON.stringify(pickupAddressPayload),
                pickup_latitude: restaurant._geoloc.lat,
                pickup_longitude: restaurant._geoloc.lng,
                // pickup_phone_number: "+15555555555",
                dropoff_address: JSON.stringify(deliveryAddress),
                dropoff_latitude: deliveryAddressLat,
                dropoff_longitude: deliveryAddressLng,
                // dropoff_phone_number: "+15555555555",
                pickup_ready_dt: orderScheduledAt ? new Date(orderScheduledAt).toISOString() : null, //"2024-12-12T14:00:00.000Z"
                pickup_deadline_dt: orderScheduledAt ? addMinutes(new Date(orderScheduledAt), 20).toISOString() : null, //"2024-12-12T14:20:00.000Z"
                // dropoff_ready_dt: "2024-12-12T14:30:00.000Z",
                dropoff_deadline_dt: orderScheduledAt ? addMinutes(new Date(orderScheduledAt), 30).toISOString() : null, //"2024-12-12T14:30:00.000Z"
                // manifest_total_value: 1000,
                external_store_id: restaurant ? restaurant.id : null,
            };

            const bandQuoteResult = await axios.post(`${getThirdPartyAPIEndpoint()}/delivery_quote`, {
                event: {
                    restaurantId: restaurant.id,
                    orderTotal: total,
                    ...payload,
                    pickupLatitude: restaurant._geoloc.lat,
                    pickupLongitude: restaurant._geoloc.lng,
                    dropoffAddress: deliveryAddress,
                    dropoffLatitude: deliveryAddressLat,
                    dropoffLongitude: deliveryAddressLng,
                },
            });
            const bandQuoteResponse = bandQuoteResult.data.data;

            if (bandQuoteResponse.kind === "error") {
                return { error: `${bandQuoteResponse.message || "Failed to get delivery quote."}`, quoteResponse: null };
            }

            if (bandQuoteResponse.provider === EOnlineDeliveryProvider.RESTAURANT_MANAGED) {
                setDeliveryAddress(deliveryAddress);
                setDeliveryAddressLat(deliveryAddressLat);
                setDeliveryAddressLng(deliveryAddressLng);
                setDeliveryProvider(EOnlineDeliveryProvider.RESTAURANT_MANAGED);
                setDeliveryDistanceMeters(bandQuoteResponse.distanceMeters ?? null);
                setUberDeliveryQuoteId(null);
                setDeliveryFeeDiscount(bandQuoteResponse.deliveryFeeDiscount || 0);
                setDeliveryFee(bandQuoteResponse.deliveryFee || 0);
                stopDeliveryQuoteTimer();

                return { error: null, quoteResponse: bandQuoteResponse as IUBER_DIRECT_QUOTE_RESPONSE };
            }

            if (bandQuoteResponse.provider !== EOnlineDeliveryProvider.UBER_DIRECT) {
                return { error: "Delivery is currently unavailable for this address.", quoteResponse: null };
            }

            const quoteResponse: IUBER_DIRECT_QUOTE_RESPONSE = bandQuoteResponse;

            if (quoteResponse.kind === "error") {
                return { error: `${quoteResponse.message || "Failed to get quote."}`, quoteResponse: null };
            } else {
                setDeliveryAddress(deliveryAddress);
                setDeliveryAddressLat(deliveryAddressLat);
                setDeliveryAddressLng(deliveryAddressLng);
                setUberDeliveryQuoteId(quoteResponse.id);
                setDeliveryProvider(EOnlineDeliveryProvider.UBER_DIRECT);
                setDeliveryDistanceMeters(bandQuoteResponse.distanceMeters ?? null);
                startDeliveryQuoteTimer();
                setDeliveryFeeDiscount(quoteResponse.deliveryFeeDiscount || 0);
                setDeliveryFee(quoteResponse.deliveryFee || 0);

                return { error: null, quoteResponse: quoteResponse };
            }
        } catch (error: any) {
            console.error("Error fetching quote:", error);
            return { error: "Network or unknown error. Please check your internet connection.", quoteResponse: null };
        }
    };

    // Returns the serialized dropoff payload to be stored on the order record.
    // The trigger uses this to book the Uber delivery after payment is confirmed.
    const getUberDirectDeliveryPayload = (): string | null => {
        if (deliveryProvider !== EOnlineDeliveryProvider.UBER_DIRECT) return null;
        if (!deliveryAddress || deliveryAddressLat == null || deliveryAddressLng == null) return null;

        const payload = {
            dropoff_address: JSON.stringify(deliveryAddress),
            dropoff_latitude: deliveryAddressLat,
            dropoff_longitude: deliveryAddressLng,
            dropoff_name: guestInformation ? guestInformation.firstName : "Tabin",
            dropoff_phone_number: guestInformation ? convertPhoneNumberToIntlFormat(guestInformation.phoneNumber) : "+64210589379",
            dropoff_notes: deliveryNotes,
            pickup_phone_number: convertPhoneNumberToIntlFormat(restaurant?.pickupPhoneNumber || "") || "+6498022557",
            pickup_ready_dt: orderScheduledAt ? new Date(orderScheduledAt).toISOString() : null,
            pickup_deadline_dt: orderScheduledAt ? addMinutes(new Date(orderScheduledAt), 20).toISOString() : null,
            dropoff_deadline_dt: orderScheduledAt ? addMinutes(new Date(orderScheduledAt), 30).toISOString() : null,
        };

        return JSON.stringify(payload);
    };

    const setPaymentMethod = (paymentMethod: EPaymentMethod | null) => {
        _setPaymentMethod(paymentMethod);
    };

    const setCovers = (covers: number) => {
        _setCovers(covers);
    };

    const setTableNumber = (tableNumber: string | null | undefined) => {
        _setTableNumber(tableNumber);
    };

    const setBuzzerNumber = (buzzerNumber: string | null) => {
        _setBuzzerNumber(buzzerNumber);
    };

    const setCustomerInformation = (customerInformation: ICustomerInformation | null) => {
        _setCustomerInformation(customerInformation);
    };

    const setProducts = (newProducts: ICartProduct[]) => {
        const newTotal = recalculateTotal(newProducts);

        _setProducts(newProducts);
        _setTotal(newTotal);
        updateCartQuantities(newProducts);
        processPromotions(newProducts, newTotal);
    };

    const addProduct = (product: ICartProduct) => {
        const { quantity: productQuantity, ...productWithoutQuantiy } = product;
        const serializedProduct = JSON.stringify(productWithoutQuantiy);

        let newProducts = products || [];

        const matchingProductIndex = newProducts.findIndex((p) => {
            const { quantity, ...pWithoutQuantiy } = p;
            return JSON.stringify(pWithoutQuantiy) === serializedProduct;
        });

        if (matchingProductIndex !== -1) {
            newProducts[matchingProductIndex].quantity += productQuantity;
        } else {
            newProducts.push(product);
        }

        const newTotal = recalculateTotal(newProducts);

        _setProducts(newProducts);
        _setTotal(newTotal);
        updateCartQuantities(newProducts);
        processPromotions(newProducts, newTotal);
        calculateDeliveryFeeDiscount(deliveryFee + deliveryFeeDiscount, newTotal);
        saveCartToCache(newProducts);
    };

    const updateProduct = (index: number, product: ICartProduct) => {
        // should never really end up here
        if (products == null) return;

        const newProducts = products;
        newProducts[index] = product;

        const newTotal = recalculateTotal(newProducts);

        _setProducts(newProducts);
        _setTotal(newTotal);
        updateCartQuantities(newProducts);
        processPromotions(newProducts, newTotal);
        calculateDeliveryFeeDiscount(deliveryFee + deliveryFeeDiscount, newTotal);
        saveCartToCache(newProducts);
    };

    const updateProductQuantity = (index: number, quantity: number) => {
        // should never really end up here
        if (products == null) return;

        const newProducts = products;
        const productAtIndex = newProducts[index];

        productAtIndex.quantity = quantity;
        newProducts[index] = productAtIndex;

        const newTotal = recalculateTotal(newProducts);

        _setProducts(newProducts);
        _setTotal(newTotal);
        updateCartQuantities(newProducts);
        processPromotions(newProducts, newTotal);
        calculateDeliveryFeeDiscount(deliveryFee + deliveryFeeDiscount, newTotal);
        saveCartToCache(newProducts);
    };

    const applyProductDiscount = (index: number, discount: number) => {
        // should never really end up here
        if (products == null) return;

        const newProducts = products;
        const productAtIndex = newProducts[index];

        productAtIndex.discount = discount;
        newProducts[index] = productAtIndex;

        const newTotal = recalculateTotal(newProducts);

        _setProducts(newProducts);
        _setTotal(newTotal);
        updateCartQuantities(newProducts);
        processPromotions(newProducts, newTotal);
    };

    const deleteProduct = (index: number) => {
        // should never really end up here
        if (products == null) return;

        let newProducts = products;
        newProducts.splice(index, 1);

        const newTotal = recalculateTotal(newProducts);

        _setProducts(newProducts);
        _setTotal(newTotal);
        updateCartQuantities(newProducts);
        processPromotions(newProducts, newTotal);
        calculateDeliveryFeeDiscount(deliveryFee + deliveryFeeDiscount, newTotal);
        saveCartToCache(newProducts);
    };

    const setNotes = (notes: string) => {
        _setNotes(notes);
    };

    const setPaymentAmounts = (amount: ICartPaymentAmounts) => {
        _setPaymentAmounts(amount);
    };

    const setPayments = (payments: ICartPayment[]) => {
        _setPayments(payments);
    };

    const setTransactionEftposReceipts = (receipt: string) => {
        _setTransactionEftposReceipts(receipt);
    };

    const setIsShownUpSellCrossSellModal = (isShownUpSellCrossSellModal: boolean) => {
        _setIsShownUpSellCrossSellModal(isShownUpSellCrossSellModal);
    };

    const setIsShownOrderThresholdMessageModal = (isShownOrderThresholdMessageModal: boolean) => {
        _setIsShownOrderThresholdMessageModal(isShownOrderThresholdMessageModal);
    };

    const updateOrderScheduledAt = (orderScheduledAt: string | null) => {
        _setOrderScheduledAt(orderScheduledAt);
    };

    const setGuestInformation = (guestInformation: IGuestInformation) => {
        _setGuestInformation(guestInformation);
    };

    const clearCart = () => {
        _setParkedOrderId(initialParkedOrderId);
        _setParkedOrderNumber(initialParkedOrderNumber);
        _setOrderType(initialOrderType);
        _setPaymentMethod(initialPaymentMethod);
        _setCovers(initialCovers);
        _setTableNumber(initialTableNumber);
        _setBuzzerNumber(initialBuzzerNumber);
        _setCustomerInformation(initialCustomerInformation);
        _setProducts(initialProducts);
        _setNotes(initialNotes);
        _setTotal(initialTotal);
        _setSurcharge(initialSurcharge);
        _setPaymentAmounts(initialPaymentAmounts);
        _setSubTotal(initialSubTotal);
        _setPayments(initialPayments);
        _setOrderTypeSurcharge(initialOrderTypeSurcharge);
        _setTransactionEftposReceipts(initialTransactionEftposReceipts);
        _setIsShownUpSellCrossSellModal(initialIsShownUpSellCrossSellModal);
        _setIsShownOrderThresholdMessageModal(initialIsShownOrderThresholdMessageModal);
        _setUserAppliedPromotionCode(initialUserAppliedPromotionCode);
        _setPromotion(initialPromotion);
        // _setAvailablePromotions(initialAvailablePromotions); //Don't need this. Otherwise, it will erase availablePromotions when you clear the cart
        _setCartCategoryQuantitiesById(initialCartCategoryQuantitiesById);
        _setCartProductQuantitiesById(initialCartProductQuantitiesById);
        _setCartModifierQuantitiesById(initialCartModifierQuantitiesById);
        _setGuestInformation(initialGuestInformation);
        _setOrderScheduledAt(initialOrderScheduledAt);
        setDeliveryAddress(initialDeliveryAddress);
        setDeliveryAddressLat(initialDeliveryAddressLat);
        setDeliveryAddressLng(initialDeliveryAddressLng);
        setDeliveryFeeDiscount(initialDeliveryFeeDiscount);
        setDeliveryFee(initialDeliveryFee);
        setUberDeliveryQuoteId(initialUberDeliveryQuoteId);
        setDeliveryNotes(initialDeliveryNotes);
        setDeliveryProvider(null);
        setDeliveryDistanceMeters(null);
        stopDeliveryQuoteTimer();
        removeCartFromCache();
    };

    return (
        <CartContext.Provider
            value={{
                // restaurant: restaurant,
                // setRestaurant: setRestaurant,
                parkedOrderId: parkedOrderId,
                setParkedOrderId: setParkedOrderId,
                parkedOrderNumber: parkedOrderNumber,
                setParkedOrderNumber: setParkedOrderNumber,
                orderType: orderType,
                setOrderType: setOrderType,
                paymentMethod: paymentMethod,
                setPaymentMethod: setPaymentMethod,
                covers: covers,
                setCovers: setCovers,
                tableNumber: tableNumber,
                setTableNumber: setTableNumber,
                buzzerNumber: buzzerNumber,
                setBuzzerNumber: setBuzzerNumber,
                customerInformation: customerInformation,
                setCustomerInformation: setCustomerInformation,
                products: products,
                cartProductQuantitiesById: cartProductQuantitiesById,
                cartModifierQuantitiesById: cartModifierQuantitiesById,
                setProducts: setProducts,
                addProduct: addProduct,
                updateProduct: updateProduct,
                updateProductQuantity: updateProductQuantity,
                applyProductDiscount: applyProductDiscount,
                deleteProduct: deleteProduct,
                clearCart: clearCart,
                notes: notes,
                setNotes: setNotes,
                promotion: promotion,
                userAppliedPromotionCode: userAppliedPromotionCode,
                setUserAppliedPromotion: setUserAppliedPromotion,
                removeUserAppliedPromotion: removeUserAppliedPromotion,
                total: total,
                surcharge: surcharge,
                subTotal: subTotal,
                paidSoFar: paymentAmounts.cash + paymentAmounts.eftpos + paymentAmounts.online + paymentAmounts.uberEats + paymentAmounts.menulog,
                orderTypeSurcharge: orderTypeSurcharge,
                paymentAmounts: paymentAmounts,
                setPaymentAmounts: setPaymentAmounts,
                payments: payments,
                setPayments: setPayments,
                transactionEftposReceipts: transactionEftposReceipts,
                setTransactionEftposReceipts: setTransactionEftposReceipts,
                isShownUpSellCrossSellModal: isShownUpSellCrossSellModal,
                isShownOrderThresholdMessageModal: isShownOrderThresholdMessageModal,
                setIsShownUpSellCrossSellModal: setIsShownUpSellCrossSellModal,
                setIsShownOrderThresholdMessageModal: setIsShownOrderThresholdMessageModal,
                orderScheduledAt: orderScheduledAt,
                updateOrderScheduledAt: updateOrderScheduledAt,
                guestInformation: guestInformation,
                setGuestInformation: setGuestInformation,
                bugFixRecalculateTotal: bugFixRecalculateTotal,
                bugFixRecalculateSubTotalAndSurcharge: bugFixRecalculateSubTotalAndSurcharge,
                deliveryAddress: deliveryAddress,
                deliveryNotes: deliveryNotes,
                deliveryProvider: deliveryProvider,
                deliveryDistanceMeters: deliveryDistanceMeters,
                deliveryFeeDiscount: deliveryFeeDiscount,
                deliveryFee: deliveryFee,
                setDeliveryAddressAndProcessQuote: setDeliveryAddressAndProcessQuote,
                getUberDirectDeliveryPayload: getUberDirectDeliveryPayload,
            }}
            children={props.children}
        />
    );
};

const useCart = () => {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error(`useCart must be used within a CartProvider`);
    }
    return context;
};

export { CartProvider, useCart };
